import { Time, Distance, Pace, UnitOfMeasure } from "./pace-fns/pace-fns";
import {
  generateCalculatorState,
  FieldId,
  CalculatorState
} from "./calculator.utility";

type Action = {
  type: Actions;
  data?: any;
};

enum Actions {
  Update = "Update",
  Clear = "Clear"
}

const defaultTime: Time = {
  hours: 0,
  minutes: 0,
  seconds: 0
};

const defaultDistance: Distance = {
  units: 0,
  unitOfMeasure: UnitOfMeasure.METER
};

const defaultPace: Pace = {
  time: defaultTime,
  distance: {units: 400, unitOfMeasure: UnitOfMeasure.METER}
};

const initialState: CalculatorState = {
  time: defaultTime,
  distance: defaultDistance,
  pace: defaultPace
};

const stateReducer = (
  state: CalculatorState = initialState,
  action: Action
) => {
  switch (action.type) {
    case Actions.Update: {
      const params = action.data as UpdateCalculatorData;
      return updateApplicationState(params);
    }
    case Actions.Clear: {
      return clearApplicationState();
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`);
    }
  }
};

export type UpdateCalculatorData = {
  pendingState: CalculatorState;
  fieldToCalculateId: FieldId;
};
export const updateCalculatorState = (data: UpdateCalculatorData): Action => ({
  type: Actions.Update,
  data
});

export const clearCalculatorState = (): Action => ({
  type: Actions.Update
});

const updateApplicationState = (
  data: UpdateCalculatorData
): CalculatorState => {
  const { fieldToCalculateId, pendingState } = data;
  const newState = generateCalculatorState(pendingState, fieldToCalculateId);
  return { ...newState };
};

const clearApplicationState = () => {
  return initialState;
};

class CalculatorStateManager {
  private state: CalculatorState;
  private stateChangeListeners: Record<string, (state: CalculatorState) => void>;
  public dispatch: (action: Action) => void;
  constructor() {
    this.state = initialState;
    this.dispatch = (action: Action) => {
      this.state = stateReducer(this.state, action);
      for(const listener of Object.values(this.stateChangeListeners)) {
          listener(this.state);
      }
    };
    this.stateChangeListeners = {};
  }
  getCalculatorState = () => {
    return this.state;
  };
  addStateListener = (stateListener: (state:CalculatorState)=>void) => {
    const id = Math.random();
    this.stateChangeListeners[id] = stateListener;
    return () => { delete  this.stateChangeListeners[id] }
  }
}

let calculatorState: CalculatorStateManager;
export const useCalculatorState = (): CalculatorStateManager => {
  if (!calculatorState) {
    calculatorState = new CalculatorStateManager();
  }
  return calculatorState;
};
